import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from '@mui/material';
import { AccessTime, LocationOn, InfoOutlined } from '@mui/icons-material';
import { EventLogType } from 'app/context/EventLogContext';
import { formatToUtcTime } from 'utils/dateUtils';
import { getEventTypeChipColor } from './eventLogUtils';

export interface EventLogCardProps {
  log: EventLogType;
  eventTypesOrder?: string[];
}

const hasAdditionalDetails = (obj: unknown): obj is Record<string, unknown> =>
  typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0;

export const EventLogCard: React.FC<EventLogCardProps> = ({ log, eventTypesOrder = [] }) => {
  const theme = useTheme();
  const [additionalDetailsOpen, setAdditionalDetailsOpen] = useState(false);
  const eventType = log.event_type || 'UNKNOWN_EVENT';
  const index = eventTypesOrder.length > 0 ? eventTypesOrder.indexOf(eventType) : -1;
  const chipColor =
    index >= 0 && eventTypesOrder.length > 0
      ? getEventTypeChipColor(
          index,
          eventTypesOrder.length,
          theme.palette.primary.main,
          theme.palette.secondary.main,
        )
      : theme.palette.primary.main;

  return (
    <Card
      variant="outlined"
      sx={{ 
        width: '100%', 
        borderRadius: 2, 
        backgroundColor: '#ffffff',
        mb: 1.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTime sx={{ fontSize: 16, color: theme.palette.text.primary }} />
              <Typography variant="subtitle2" color="textSecondary">
                {log.created_date ? formatToUtcTime(log.created_date) : 'No Timestamp'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn sx={{ fontSize: 16, color: theme.palette.text.primary }} />
              <Typography variant="subtitle2" color="textSecondary">
                {log.originating_ip || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={eventType}
            size="small"
            sx={{ 
              fontWeight: 600,
              minWidth: '100px',
              height: '24px',
              backgroundColor: chipColor,
              color: '#fff',
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={1}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              pl: 0.5,
              flex: 1,
            }}
          >
            {log.message || 'No message provided.'}
          </Typography>
          {hasAdditionalDetails(log.additional_details) && (
            <Tooltip title="Additional details">
              <IconButton
                size="small"
                onClick={() => setAdditionalDetailsOpen(true)}
                sx={{
                  color: 'text.secondary',
                  p: 0.5,
                  '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' },
                }}
                aria-label="Show additional details"
              >
                <InfoOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
      <Dialog
        open={additionalDetailsOpen}
        onClose={() => setAdditionalDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', py: 1.5 }}>
          Additional details
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              overflow: 'auto',
              maxHeight: 400,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(log.additional_details, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EventLogCard;
